import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Grid, Group, Title, Modal, Stack, Text, Space, useMantineTheme, Drawer, Collapse } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";

import { useCreateUserMutation, useDeleteUserMutation, useGetUserByIdQuery, useGetUsersQuery, useRestoreUserMutation, useUpdateUserMutation } from "../../api/user.api";
import { ListUserDto, MutableUserDto, UserDto } from "../../models/user.models";
import { UserList } from "../../Components/Users/UserList";
import { UserDetailView } from "../../Components/Users/UserDetailView";
import UserEditView from "../../Components/Users/UserEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";

const UsersPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ userId?: string }>();
	const isEditingUser = useMatch("/users/:userId/edit") !== null;
	const { SearchBox, visible: searchVisible } = useSearch();

	const isNewUser = params.userId === "new";
	const userIdFromParams = params.userId || null;

	const { data: users, isPending: isLoadingUsers } = useGetUsersQuery();
	const [selectedUser, setSelectedUser] = useState<ListUserDto | null>(null);
	const { data: user, isPending: isLoadingUser } = useGetUserByIdQuery(selectedUser?.UserId || userIdFromParams || null);
	
	const [discardChangesModalOpened, { open: openDiscardUserChangesModal, close: closeDiscardUserChangesModal }] = useDisclosure(false);
	const [deleteUserModalOpen, { open: openDeleteUserModal, close: closeDeleteUserModal }] = useDisclosure(false);
	const [restoreUserModalOpen, { open: openRestoreUserModal, close: closeRestoreUserModal }] = useDisclosure(false);
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);
	const [isUserChanged, setIsUserChanged] = useState(false);
	const [requestedUserId, setRequestedUserId] = useState<string | null>(null);
	
	const { mutate: createUser, isPending: isCreatingUser } = useCreateUserMutation();
	const { mutate: updateUser, isPending: isUpdatingUser } = useUpdateUserMutation();
	const { mutate: deleteUser } = useDeleteUserMutation();
	const { mutate: restoreUser, isPending: isRestoringUser } = useRestoreUserMutation();

	const isSavingUser = isCreatingUser || isUpdatingUser;


	useEffect(() => {
		if (userIdFromParams) {
			const user = users?.find((usr: ListUserDto) => usr.UserId === userIdFromParams);
			if (user) {
				setSelectedUser?.(user);
				if (isMobile) {
					openDetailViewDrawer();
				}
			}
		} else if (users && users.length > 0) {
			setSelectedUser?.(users[0]);
		}

	}, [userIdFromParams, users, isMobile]);

	useEffect(() => {
		if (!userIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleNewUser = () => {
		setIsUserChanged(false);
		setSelectedUser(null);
		navigate(`/users/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleEditUser = () => {
		navigate(`/users/${selectedUser?.UserId}/edit`);
	};

	const handleTabSelected = (tab: string | null) => {
		navigate(`/${tab}`)
	};

	const handleUserSelect = (user: ListUserDto | null) => {
		if (!user)
			return;

		setRequestedUserId(user.UserId);

		if (isUserChanged && !discardChangesModalOpened) {
			openDiscardUserChangesModal();
			return;
		}

		setSelectedUser(user);
		setIsUserChanged(false);
		navigate(`/users/${user.UserId}`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSaveUser = (values: MutableUserDto) => {
		if (isNewUser) {
			createUser(values, {
				onSuccess: (response) => {
					setIsUserChanged(false);
					navigate(`/users/${response.data.UserId}`, { replace: true });
				}
			});
		} else {
			updateUser(values, {
				onSuccess: () => {
					setIsUserChanged(false);
					// history.replace(`/users/${selectedUser?.UserId}`);
					navigate(-1);
				}
			});
		}
	}

	const handleDeleteUser = () => {
		if (!user) return;

		closeDeleteUserModal();
		deleteUser(user, {
			onSuccess: () => {
				setSelectedUser(null);
				navigate(`/users`, { replace: true });
			}
		});
	}

	const handleRestoreUser = () => {
		if (!user) return;

		closeRestoreUserModal();
		restoreUser(user, {
			onSuccess: () => {

			}
		});
	}

	const handleCloseUser = () => {
		if (isUserChanged && !discardChangesModalOpened) {
			openDiscardUserChangesModal();
		} else {
			closeDiscardUserChangesModal();
			setIsUserChanged(false);
			navigate(-1);

			if (isMobile && isNewUser) {
				closeDetailViewDrawer();
			}
		}
	}

	const handleUserChanged = (changed: boolean) => {
		setIsUserChanged(changed);
	};

	const handleDiscardUserChanges = () => {
		closeDiscardUserChangesModal();
		setIsUserChanged(false);

		if (isMobile && isNewUser) {
			closeDetailViewDrawer();
		}

		if (requestedUserId && requestedUserId !== selectedUser?.UserId) {
			navigate(`/users/${requestedUserId}`);
			setRequestedUserId(null);
		} else {
			navigate(-1);
		}
	}

	const handleCancelUserDiscard = () => {
		closeDiscardUserChangesModal();
	};

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<DeleteUserModal
			user={user}
			open={deleteUserModalOpen}
			onDelete={handleDeleteUser}
			onClose={closeDeleteUserModal}
		/>

		<RestoreUserModal
			user={user}
			open={restoreUserModalOpen}
			onRestore={handleRestoreUser}
			onClose={closeRestoreUserModal}
		/>

		<DiscardUserChangesModal
			open={discardChangesModalOpened}
			onDiscard={handleDiscardUserChanges}
			onClose={handleCancelUserDiscard}
		/>

		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{isEditingUser || isNewUser ? <>
				<UserEditView user={!isNewUser ? user : null} onSave={handleSaveUser} onClose={handleCloseUser} isSaving={isSavingUser} onChanged={handleUserChanged} />
			</> : (
				<UserDetailView user={user} isLoading={isLoadingUser} onEdit={handleEditUser} onClose={handleDetailViewDrawerClose} onDelete={openDeleteUserModal} onRestore={openRestoreUserModal} isRestoring={isRestoringUser} />
			)}	
		</Drawer>

		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}
		</Collapse>

		<Group justify="space-between">
			<Title order={2}>Users & Teams</Title>
			<Group>
				<RoleTemplate role={AzureADRoles.Admin}>
					<Button onClick={handleNewUser} radius="xl" leftSection={<IconPlus />}>New User</Button>
				</RoleTemplate>
			</Group>
		</Group>

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!users || users.length === 0) && !isNewUser && !isEditingUser) ? 12 : 5 }}>
				<UserList users={users} isLoading={isLoadingUsers} selectedUser={selectedUser} setSelectedUser={handleUserSelect} setSelectedTab={handleTabSelected} />
			</Grid.Col>
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{isEditingUser || isNewUser ? <>
					<UserEditView user={!isNewUser ? user : null} onSave={handleSaveUser} onClose={handleCloseUser} isSaving={isSavingUser} onChanged={handleUserChanged} />
				</> : users && users.length > 0 ? (
					<UserDetailView user={user} isLoading={isLoadingUser} onEdit={handleEditUser} onDelete={openDeleteUserModal} onRestore={openRestoreUserModal} isRestoring={isRestoringUser} />
				) : <></>}
			</Grid.Col>
		</Grid>
	</>
}

export default UsersPage

const DiscardUserChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			<Text>If you leave now, you’ll lose unsaved changes to this user.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteUserModal = ({ user, open, onDelete, onClose }: { user?: UserDto, open: boolean, onDelete: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Delete User</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete {user?.DisplayName}?</Text>
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>
	);
}

const RestoreUserModal = ({ user, open, onRestore, onClose }: { user?: UserDto, open: boolean, onRestore: () => void, onClose: () => void }) => {
	return (
		<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Restore User</Text>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to restore {user?.DisplayName}?</Text>
				<Space h="xs" />
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button variant="filled" onClick={onRestore} radius="lg">Restore</Button>
				</Group>
			</Stack>
		</Modal>
	);
}